import { LISTING_LIMITS, ReturnPolicyType } from '../constants.js';
import { isValidPriceCents, stripHtml, ValidationField, } from './shared.js';
// Required/length only — no uniqueness, since that needs a list of the
// shop's other profile names that only the caller has. The server checks
// uniqueness via the DB's unique constraint (see DuplicateProfileNameError
// in shopManager.service.ts) rather than this check, so it calls this
// directly; the client calls `validateProfileNameUniqueness` too, for
// immediate feedback before it ever hits the server.
export const validateProfileNameFields = (name) => {
    const trimmed = name.trim();
    if (!trimmed)
        return { field: ValidationField.Name, message: 'Name is required.' };
    if (trimmed.length > LISTING_LIMITS.maxProfileNameLength) {
        return {
            field: ValidationField.Name,
            message: `Name must be ${LISTING_LIMITS.maxProfileNameLength} characters or fewer.`,
        };
    }
    return null;
};
export const validateProfileNameUniqueness = (name, existingNames) => {
    const trimmed = name.trim();
    if (existingNames.some((n) => n.toLowerCase() === trimmed.toLowerCase())) {
        return {
            field: ValidationField.Name,
            message: 'A profile with this name already exists.',
        };
    }
    return null;
};
export const validateDayRange = (minDays, maxDays) => {
    const min = typeof minDays === 'number' ? minDays : parseInt(minDays, 10);
    const max = typeof maxDays === 'number' ? maxDays : parseInt(maxDays, 10);
    if (!Number.isInteger(min) ||
        !Number.isInteger(max) ||
        min < 1 ||
        max < min)
        return null;
    return { min, max };
};
// Called directly by the server (no existingNames — see the comment on
// validateProfileNameFields).
export const validateProcessingProfileFields = (input) => {
    const errors = [];
    const nameError = validateProfileNameFields(input.name);
    if (nameError)
        errors.push(nameError);
    if (!validateDayRange(input.minDays, input.maxDays)) {
        errors.push({
            field: ValidationField.Days,
            message: 'Enter a valid range (min ≤ max, both ≥ 1).',
        });
    }
    return errors;
};
// Called by the client, which has the shop's existing profile names loaded
// in memory and can surface a duplicate-name error immediately.
export const validateProcessingProfileInput = (input) => {
    const errors = validateProcessingProfileFields(input);
    const uniquenessError = validateProfileNameUniqueness(input.name, input.existingNames);
    if (uniquenessError)
        errors.push(uniquenessError);
    return errors;
};
export const validateShippingProfileFields = (input) => {
    const errors = [];
    const nameError = validateProfileNameFields(input.name);
    if (nameError)
        errors.push(nameError);
    if (!/^\d{5}$/.test(input.originZip.trim())) {
        errors.push({
            field: ValidationField.OriginZip,
            message: 'Enter a valid 5-digit zip code.',
        });
    }
    if (input.isFlatRate &&
        (input.flatShippingRateCents == null ||
            !isValidPriceCents(input.flatShippingRateCents, LISTING_LIMITS.maxPriceCents))) {
        errors.push({ field: ValidationField.FlatRate, message: 'Enter a valid price.' });
    }
    if (!validateDayRange(input.shippingDaysMin, input.shippingDaysMax)) {
        errors.push({
            field: ValidationField.Days,
            message: 'Enter a valid range (min ≤ max, both ≥ 1).',
        });
    }
    return errors;
};
export const validateShippingProfileInput = (input) => {
    const errors = validateShippingProfileFields(input);
    const uniquenessError = validateProfileNameUniqueness(input.name, input.existingNames);
    if (uniquenessError)
        errors.push(uniquenessError);
    return errors;
};
export const validateReturnProfileFields = (input) => {
    const errors = [];
    const nameError = validateProfileNameFields(input.name);
    if (nameError)
        errors.push(nameError);
    if (input.policyType !== ReturnPolicyType.NO_RETURNS) {
        const days = typeof input.returnWindowDays === 'number'
            ? input.returnWindowDays
            : parseInt(String(input.returnWindowDays ?? ''), 10);
        if (!Number.isInteger(days) || days < 1) {
            errors.push({
                field: ValidationField.WindowDays,
                message: 'Window is required.',
            });
        }
    }
    if (input.policyType === ReturnPolicyType.CUSTOM) {
        const stripped = stripHtml(input.policyDescrRichText ?? '');
        if (!stripped) {
            errors.push({
                field: ValidationField.CustomText,
                message: 'Policy details are required.',
            });
        }
        else if (stripped.length > LISTING_LIMITS.maxReturnPolicyChars) {
            errors.push({
                field: ValidationField.CustomText,
                message: `Policy must be ${LISTING_LIMITS.maxReturnPolicyChars.toLocaleString()} characters or fewer.`,
            });
        }
    }
    return errors;
};
export const validateReturnProfileInput = (input) => {
    const errors = validateReturnProfileFields(input);
    const uniquenessError = validateProfileNameUniqueness(input.name, input.existingNames);
    if (uniquenessError)
        errors.push(uniquenessError);
    return errors;
};
export const validatePersonalizationProfileFields = (input) => {
    const errors = [];
    const nameError = validateProfileNameFields(input.name);
    if (nameError)
        errors.push(nameError);
    if (input.costCents == null ||
        !isValidPriceCents(input.costCents, LISTING_LIMITS.maxPriceCents)) {
        errors.push({
            field: ValidationField.Cost,
            message: 'Cost is required.',
        });
    }
    const trimmedHelperText = (input.helperText ?? '').trim();
    if (trimmedHelperText.length >
        LISTING_LIMITS.maxPersonalizationHelperTextLength) {
        errors.push({
            field: ValidationField.HelperText,
            message: `Helper text must be ${LISTING_LIMITS.maxPersonalizationHelperTextLength} characters or fewer.`,
        });
    }
    return errors;
};
export const validatePersonalizationProfileInput = (input) => {
    const errors = validatePersonalizationProfileFields(input);
    const uniquenessError = validateProfileNameUniqueness(input.name, input.existingNames);
    if (uniquenessError)
        errors.push(uniquenessError);
    return errors;
};
